// services/traitCategoryService.ts
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp
} from "firebase/firestore";
import { db } from "../firebase/config";
import { TraitCategory } from "../types";

const CATEGORIES_COLLECTION = "traitCategories";

export const getTraitCategories = async (): Promise<TraitCategory[]> => {
  try {
    const q = query(collection(db, CATEGORIES_COLLECTION), orderBy("name", "asc"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      name: doc.data().name,
      createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    } as TraitCategory));
  } catch (error) {
    console.error("Error fetching trait categories:", error);
    throw error;
  }
};

export const getTraitCategoryById = async (id: string): Promise<TraitCategory | null> => {
  try {
    const docRef = doc(db, CATEGORIES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        name: docSnap.data().name,
        createdAt: docSnap.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: docSnap.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as TraitCategory;
    }
    return null;
  } catch (error) {
    console.error("Error fetching trait category:", error);
    throw error;
  }
};

export const createTraitCategory = async (categoryData: { name: string }): Promise<string> => {
  try {
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, CATEGORIES_COLLECTION), {
      name: categoryData.name,
      createdAt: now,
      updatedAt: now,
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating trait category:", error);
    throw error;
  }
};

export const updateTraitCategory = async (id: string, categoryData: { name: string }): Promise<void> => {
  try {
    const docRef = doc(db, CATEGORIES_COLLECTION, id);
    await updateDoc(docRef, {
      name: categoryData.name,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error updating trait category:", error);
    throw error;
  }
};

export const deleteTraitCategory = async (id: string): Promise<void> => {
  try {
    const traitsQuery = query(collection(db, "traits"), where("categoryId", "==", id));
    const traitsSnapshot = await getDocs(traitsQuery);
    
    if (!traitsSnapshot.empty) {
      throw new Error(`Cannot delete category: ${traitsSnapshot.size} trait(s) are using it`);
    }
    
    const docRef = doc(db, CATEGORIES_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting trait category:", error);
    throw error;
  }
};